import { useState } from 'react';
import { Plus } from 'lucide-react';
import { KanbanBoard } from '@/components/Kanban/KanbanBoard';
import { CandidateDrawer } from '@/components/Candidate/CandidateDrawer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CandidateForm } from '@/components/Candidate/CandidateForm';
import { Button } from '@/components/ui/button';
import { useCandidates, useCreateCandidate } from '@/hooks/useCandidates';
import { useToast } from '@/components/ui/toast';
import type { Candidate, CreateCandidateDTO } from '@/types';

export function KanbanPage() {
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: candidates = [], isLoading, error } = useCandidates();
  const createCandidate = useCreateCandidate();
  const { toast } = useToast();

  async function handleCreate(dto: CreateCandidateDTO) {
    try {
      await createCandidate.mutateAsync(dto);
      setCreateOpen(false);
      toast({ title: 'Candidato cadastrado com sucesso', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao cadastrar candidato', description: 'Tente novamente.', variant: 'error' });
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between border-b border-border bg-bg-surface px-5 py-3">
        <h1 className="text-sm font-semibold text-text-primary">Funil de Recrutamento</h1>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-3 w-3" />
          Novo Candidato
        </Button>
      </div>

      <div className="flex-1 overflow-x-auto p-5">
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm text-text-muted">Carregando...</span>
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm text-status-red">{error.message}</span>
          </div>
        )}
        {!isLoading && !error && (
          <KanbanBoard
            candidates={candidates}
            onCardClick={(c) => setSelectedCandidate(c)}
          />
        )}
      </div>

      <CandidateDrawer
        candidateId={selectedCandidate?.id ?? null}
        open={!!selectedCandidate}
        onClose={() => setSelectedCandidate(null)}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Novo Candidato</DialogTitle>
          </DialogHeader>
          <CandidateForm
            onSubmit={(dto) => handleCreate(dto as CreateCandidateDTO)}
            onCancel={() => setCreateOpen(false)}
            isLoading={createCandidate.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
